/**
 * Responsibility: one search result in full.
 *
 * The frame, the size and the scrolling rules all come from
 * `CardDialogShell`, which every card dialog in the app shares - the three
 * had drifted into three sizes and three scrolling behaviours, and the
 * shell is the single definition now. This file supplies only the tabs.
 *
 * The dialog does not scroll. Anything unbounded - a hundred sales, forty
 * listings - scrolls inside its own region, which is what the tabs are for
 * in the first place.
 */
import { PriceAgeBadge } from './PriceAgeBadge';
import { daysOld } from './priceAge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import LaunchIcon from '@mui/icons-material/Launch';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  useMarketCard,
  useSaved,
  useToggleLike,
  useToggleWatch,
} from '../../api/hooks/useMarket';
import type { MarketResult } from '../../api/client';
import type { ReactNode } from 'react';
import { usePageState } from '../../hooks/usePageState';
import { CardChartArea } from '../cards/CardChartArea';
import { CardDialogShell, ScrollRegion } from '../cards/CardDialogShell';
import { CardSalesTab } from '../cards/CardSalesTab';
import { Figure, Outcome, Row, EM_DASH } from '../grading/CardFigures';
import { ImageCarousel } from '../grading/ImageCarousel';
import { ImageLightbox } from '../grading/ImageLightbox';
import { ListingsList } from '../grading/ListingsList';
import { money, multiple } from '../grading/money';

type MarketCardDialogProps = {
  card: MarketResult | null;
  onClose: () => void;
  onAdd: (card: MarketResult, grade: string) => void;
  /**
   * A row has been clicked but its card has not arrived yet.
   *
   * The discovery and catalogue boards pass the *fetched* card as `card`,
   * so until the fetch returned there was nothing to render and clicking
   * a row did visibly nothing. On a sports card that had never been read,
   * behind the catalogue's pacing, that silence lasted several seconds
   * and read as a broken button. The dialog opens on the click now and
   * says it is loading.
   */
  pending?: boolean;
  /**
   * Page-specific controls for this card, under the figures.
   *
   * A slot rather than knowledge of sub-collections: this dialog is
   * shared by search, likes and watching, and only one of them can file
   * a card into a grouping. Teaching it which page it is on would put
   * that branch in the wrong place.
   */
  extra?: ReactNode;
  /**
   * Page-specific buttons for the actions row, beside Like and Close.
   *
   * Separate from `extra`, which sits under the figures. A control that
   * *does* something belongs where the other things that do something
   * are: put under the picture it reads as a caption, and the operator
   * said so in exactly those terms.
   */
  moreActions?: ReactNode;
};

const IMAGE_WIDTH = 150;
const GALLERY_WIDTH = 280;

export function MarketCardDialog({
  card,
  onClose,
  onAdd,
  pending = false,
  extra,
  moreActions,
}: MarketCardDialogProps) {
  const [tab, setTab] = usePageState('market.dialog.tab', 0);
  const [zoom, setZoom] = usePageState<string | null>('market.dialog.zoom', null);
  const detail = useMarketCard(card?.key ?? null);
  // Read here rather than passed in: every call site would otherwise have to
  // thread two sets and two mutations through, and three of them already
  // forget one or the other.
  const saved = useSaved();
  const likedKeys = new Set((saved.data?.likes ?? []).map((row) => row.product_key));
  const watchedKeys = new Set((saved.data?.watching ?? []).map((row) => row.product_key));
  const toggleLike = useToggleLike(likedKeys);
  const toggleWatch = useToggleWatch(watchedKeys);
  const isLiked = card !== null && likedKeys.has(card.key);
  const isWatched = card !== null && watchedKeys.has(card.key);

  if (!card) {
    return pending ? (
      <CardDialogShell
        open
        title="Reading the card"
        tab={0}
        onTab={() => undefined}
        onClose={onClose}
        tabs={[
          {
            label: 'Card',
            content: (
              <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ height: '100%' }}>
                <CircularProgress />
                <Typography variant="body2" color="text.secondary">
                  Fetching prices and listings from the catalogue.
                </Typography>
              </Stack>
            ),
          },
        ]}
      />
    ) : null;
  }

  const sales = detail.data?.sales ?? [];
  const rawListings = detail.data?.raw_listings ?? [];
  const slabListings = detail.data?.graded_listings ?? [];
  // **The live page first, the catalogue's render only until it arrives.**
  // Search answers from the catalogue now, and a set page knows one
  // photograph: the render. The card's own page carries photographs of real
  // copies, which is what shows condition and is the reason for opening it.
  // Falling back the other way round left the dialog showing a thumbnail
  // after the real images had loaded.
  // **The prices on screen have an age, and it is worth saying.** The list
  // comes from the catalogue and is as old as the last crawl of that set;
  // the dialog fetches the card's own page, so once that lands the figures
  // are current. The badge says which of the two is being looked at.
  const priced = detail.data?.result.priced_at ?? card.priced_at;
  const age = daysOld(priced);
  const livePhotos = detail.data?.result.images ?? [];
  const photos =
    livePhotos.length > 0
      ? livePhotos
      : [card.image_large_url ?? '', ...card.images].filter(Boolean);

  const figures = (
    <Stack spacing={2} sx={{ minWidth: 0, height: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ minWidth: 0 }}>
        <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ flexShrink: 0 }}>
          <PriceAgeBadge days={age} live={detail.isSuccess} />
          <ImageCarousel
            images={photos}
            alt={card.name}
            width={IMAGE_WIDTH}
            onZoom={setZoom}
          />
        </Stack>

        <Stack spacing={2} sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}>
          <Row>
            <Figure label="Raw cost" value={money(card.raw_price)} hint="ungraded, sold" />
            <Figure
              label={card.best_label ?? 'Best grade'}
              value={money(card.best_price)}
              hint={card.volume ?? 'no sales recorded'}
            />
            <Figure
              label="Multiple"
              value={card.multiple ? multiple(card.multiple) : EM_DASH}
              hint="best grade over raw"
            />
            <Outcome
              label="If it gems"
              value={card.best_case_profit}
              hint={card.total_cost ? `${money(card.total_cost)} all in` : 'not computable'}
            />
          </Row>

          <Row>
            <Figure
              label="Sales seen"
              value={String(card.sales_count)}
              hint={card.newest_sale ? `newest ${card.newest_sale}` : 'none dated'}
            />
            <Figure
              label="Recent average"
              value={money(card.recent_average)}
              hint="last 90 days"
            />
            <Figure
              label="Break even"
              value={money(card.break_even_raw)}
              hint="pay up to this raw"
            />
            <Figure
              label="Catalogue"
              value={card.category === 'SPORTS' ? 'SportsCardsPro' : 'PriceCharting'}
              hint="completed sales"
            />
          </Row>
        </Stack>
      </Stack>

      {extra ? <Box sx={{ flexShrink: 0 }}>{extra}</Box> : null}

      <Divider />

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <CardChartArea
          sales={sales}
          loading={detail.isLoading}
          preferredGrade={card.best_label}
        />
      </Box>

      <Stack direction="row" justifyContent="center" sx={{ flexShrink: 0 }}>
        <Link
          href={card.url}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          underline="hover"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          Open on {card.category === 'SPORTS' ? 'SportsCardsPro' : 'PriceCharting'}
          <LaunchIcon sx={{ fontSize: 12 }} />
        </Link>
      </Stack>
    </Stack>
  );

  return (
    <>
      <CardDialogShell
        open
        tab={tab}
        onTab={setTab}
        onClose={onClose}
        title={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <span>{card.name}</span>
            <Chip
              size="small"
              variant="outlined"
              label={card.category === 'SPORTS' ? 'Sports' : 'Trading card'}
            />
          </Stack>
        }
        tabs={[
          { label: 'Figures', content: figures },
          {
            label: sales.length > 0 ? `Sales (${sales.length})` : 'Sales',
            content: (
              <CardSalesTab
                grades={card.grades}
                sales={sales}
                sourceUrl={card.url}
                loading={detail.isLoading}
                preferredGrade={card.best_label}
              />
            ),
          },
          {
            label: rawListings.length > 0 ? `Raw cards (${rawListings.length})` : 'Raw cards',
            content: (
              <ScrollRegion>
                <ListingsList
                  listings={rawListings}
                  loading={detail.isLoading}
                  emptyMessage="No raw copies listed right now. Without eBay credentials configured this stays empty."
                />
              </ScrollRegion>
            ),
          },
          {
            label: slabListings.length > 0 ? `Slabs (${slabListings.length})` : 'Slabs',
            content: (
              <ScrollRegion>
                <ListingsList
                  listings={slabListings}
                  loading={detail.isLoading}
                  gradeFilter
                  emptyMessage="No graded copies listed right now. Without eBay credentials configured this stays empty."
                />
              </ScrollRegion>
            ),
          },
          {
            label: photos.length > 1 ? `Photos (${photos.length})` : 'Photos',
            content: (
              <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <ImageCarousel
                  images={photos}
                  alt={card.name}
                  width={GALLERY_WIDTH}
                  onZoom={setZoom}
                />
                <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                  The catalogue render first, then photographs of copies that actually sold.
                </Typography>
              </Stack>
            ),
          },
        ]}
        actions={
          <>
            {/* The same two decisions the row offers, because the dialog is
                where the decision is usually actually made - you open a card
                to look at its sales before keeping it. */}
            <Button
              size="small"
              startIcon={
                isLiked ? (
                  <FavoriteIcon fontSize="small" color="error" />
                ) : (
                  <FavoriteBorderIcon fontSize="small" />
                )
              }
              disabled={toggleLike.isPending}
              onClick={() => toggleLike.mutate(card.key)}
            >
              {isLiked ? 'Liked' : 'Like'}
            </Button>
            <Button
              size="small"
              startIcon={
                isWatched ? (
                  <VisibilityIcon fontSize="small" color="primary" />
                ) : (
                  <VisibilityOutlinedIcon fontSize="small" />
                )
              }
              disabled={toggleWatch.isPending}
              onClick={() => toggleWatch.mutate(card.key)}
            >
              {isWatched ? 'Watching' : 'Watch'}
            </Button>
            <Button size="small" onClick={() => onAdd(card, card.best_label ?? 'Ungraded')}>
              Add to collection
            </Button>
            {moreActions}
            <Button size="small" variant="contained" onClick={onClose}>
              Close
            </Button>
          </>
        }
      />

      {zoom ? <ImageLightbox src={zoom} alt={card.name} onClose={() => setZoom(null)} /> : null}
    </>
  );
}
